import { Box, Center, Flex, Group, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type {
	ApiListPolicyResultsQueryParams,
	PolicyIssueOut,
	PolicyOut,
	PolicyRuleType,
	PolicyViolationOut,
} from '@repo/api-codegen';
import {
	apiQueryKey,
	fetchApiListPolicyResults,
	useApiAcknowledgePolicyResult,
	useApiIgnorePolicyResult,
	useApiListPolicyIssues,
	useApiListPolicyResults,
	useApiReopenPolicyResult,
	useApiResolvePolicyResult,
} from '@repo/api-codegen';
import { Banner, Button, Icon, Text, Title } from '@repo/foundations';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import type { ISecodaEntity } from '../../../../api';
import { queryClient } from '../../../../api';
import { resourceCatalogQueryKeyFactory } from '../../../../api/hooks/resourceCatalog/constants';
import entityDrawerStore from '../../../../components/EntityDrawer/store';
import { IntegrationSelector } from '../../../../components/EntityModal/Metadata/IntegrationSelector';
import { TagSelector } from '../../../../components/EntityModal/Metadata/TagSelector';
import { generateCsvFromResults } from '../../../../components/TableV2/TableV2.utils';
import { saveBlob } from '../../../../lib/models/misc';
import { useFeatureFlags } from '../../../../utils/featureFlags';
import { formatDateTimeWithAt } from '../../../../utils/time';

import { ResourceDisplay } from '../../../PoliciesPage/render';
import PolicySection from '../../PolicySection';
import { PolicyIssueDrawer } from './PolicyIssueDrawer/PolicyIssueDrawer';
import { PolicyViolationsTable } from './PolicyViolationTable';
import policyIssueStore from './store';
import { useStyles } from './styles';
import { ViolationTableFooter } from './ViolationTableFooter';

interface PolicyIssuesProps {
	policy: PolicyOut;
	readOnly?: boolean;
}

type PolicyIssueWithEphemeralResolved = PolicyIssueOut & {
	resolved: boolean;
};

type FilterType = 'integration' | 'tag' | null;

export const PolicyIssues = observer(
	({ policy, readOnly = false }: PolicyIssuesProps) => {
		const { policyV2, dqsV2, dataScanning } = useFeatureFlags();
		const { classes, theme } = useStyles();
		const [page, setPage] = useState(1);

		const [integrationId, setIntegrationId] = useState<string>('');
		const [tagId, setTagId] = useState<string>('');
		const [activeFilterType, setActiveFilterType] = useState<FilterType>(null);

		const handleClearFilters = useCallback(() => {
			setIntegrationId('');
			setTagId('');
			setActiveFilterType(null);
		}, []);

		const queryParams = useMemo(() => {
			const params: Record<string, string | number> = { page };

			if (integrationId) {
				params.integration_id = integrationId;
			}
			if (tagId) {
				params.tag_id = tagId;
			}
			return params;
		}, [page, integrationId, tagId]);

		const { data: violationsData, isLoading: violationsLoading } =
			useApiListPolicyResults(
				{
					pathParams: {
						policyId: policy.id,
					},
					queryParams,
				},
				{
					enabled: policyV2,
				}
			);

		const { data: issuesData, isLoading: issuesLoading } =
			useApiListPolicyIssues(
				{
					pathParams: {
						policyId: policy.id,
					},
					queryParams,
				},
				{
					enabled: !policyV2,
				}
			);

		const [previousFetched, setPreviousFetched] = useState<PolicyIssueOut[]>(
			[]
		);

		const issues: PolicyIssueWithEphemeralResolved[] = useMemo(() => {
			if (policyV2) return [];

			const newIssues =
				issuesData?.results.map((issue) => ({
					...issue,
					resolved: false,
				})) ?? [];

			for (const issue of previousFetched) {
				if (!newIssues.find((i) => i.id === issue.id)) {
					newIssues.push({
						...issue,
						resolved: true,
					});
				}
			}

			setPreviousFetched(issuesData?.results ?? []);
			return newIssues.sort((a, b) =>
				dayjs(b.occured_at).diff(dayjs(a.occured_at))
			);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [issuesData?.results, policyV2]);

		const handleResourceClick = useCallback(
			async (
				id: string,
				parent_result_id?: string | null,
				parent_url?: string | null,
				policyType?: PolicyRuleType,
				violations?: PolicyViolationOut[]
			) => {
				if (policyType === 'PII_SCAN' && dataScanning) {
					const selectedId = policyIssueStore.selectedEntityId;
					if (selectedId === id) {
						policyIssueStore.setSelectedEntityId(null);
						policyIssueStore.setColumnViolations(null, parent_url);
					} else {
						if (parent_result_id) {
							const fetchParams: ApiListPolicyResultsQueryParams = {
								page: 1,
								parent_id: parent_result_id,
							};
							await fetchApiListPolicyResults({
								pathParams: {
									policyId: policy.id,
								},
								queryParams: fetchParams,
							}).then((data) => {
								policyIssueStore.setColumnViolations(data.results, parent_url);
							});
						}
						policyIssueStore.setSelectedEntityId(id);
					}
				} else if (dataScanning && policyType !== 'PII_SCAN') {
					policyIssueStore.setSelectedEntityId(null);
					policyIssueStore.openPolicyIssueSidebarWithId(
						policy,
						violations ?? [],
						id
					);
				} else {
					entityDrawerStore.openEntityDrawerById(true, id, undefined, () => {
						queryClient.invalidateQueries({
							queryKey: resourceCatalogQueryKeyFactory.allLists(),
						});
						queryClient.invalidateQueries({
							queryKey: apiQueryKey('policy/{policy_id}/issues', {
								policy_id: policy.id,
							}),
						});
						if (policyV2) {
							queryClient.invalidateQueries({
								queryKey: apiQueryKey('policy/{policy_id}/results', {
									policy_id: policy.id,
								}),
							});
						}
					});
				}
			},
			[policyV2, dataScanning, policy]
		);

		const { mutateAsync: acknowledgePolicyViolation } =
			useApiAcknowledgePolicyResult({});
		const { mutateAsync: resolvePolicyViolation } = useApiResolvePolicyResult(
			{}
		);
		const { mutateAsync: ignorePolicyViolation } = useApiIgnorePolicyResult();
		const { mutateAsync: reopenPolicyViolation } = useApiReopenPolicyResult();
		const onSuccessCallback = useCallback(
			async (type: 'acknowledged' | 'resolved' | 'ignored' | 're-opened') => {
				if (policy) {
					await queryClient.invalidateQueries({
						queryKey: apiQueryKey('policy/{policy_id}/results', {
							policy_id: policy.id,
						}),
					});
					queryClient.invalidateQueries({
						queryKey: apiQueryKey('activity_log/resource_logs'),
					});
				}
				policyIssueStore.closeEntityDrawer();
				policyIssueStore.setColumnViolations(null, null);
				policyIssueStore.setSelectedEntityId(null);
				showNotification({
					title: `Successfully ${type} issue`,
					message: `The issue has been ${type}`,
					color: 'green',
					icon: <Icon name="check" color="text/success-on-fill/default" />,
				});
			},
			[policy]
		);

		const handleAcknowledgeOnClick = useCallback(
			async (resultId: string) => {
				await acknowledgePolicyViolation(
					{
						pathParams: {
							resultId,
						},
					},
					{
						onSuccess: () => onSuccessCallback('acknowledged'),
					}
				);
			},
			[acknowledgePolicyViolation, onSuccessCallback]
		);

		const handleResolveOnClick = useCallback(
			async (resultId: string) => {
				await resolvePolicyViolation(
					{
						pathParams: {
							resultId: resultId,
						},
					},
					{
						onSuccess: () => onSuccessCallback('resolved'),
					}
				);
			},
			[resolvePolicyViolation, onSuccessCallback]
		);

		const handleIgnoreOnClick = useCallback(
			async (resultId: string) => {
				await ignorePolicyViolation(
					{
						pathParams: {
							resultId,
						},
					},
					{
						onSuccess: () => onSuccessCallback('ignored'),
					}
				);
			},
			[ignorePolicyViolation, onSuccessCallback]
		);

		const handleReopenOnClick = useCallback(
			async (resultId: string) => {
				await reopenPolicyViolation(
					{
						pathParams: {
							resultId,
						},
					},
					{
						onSuccess: () => onSuccessCallback('re-opened'),
					}
				);
			},
			[reopenPolicyViolation, onSuccessCallback]
		);

		const handleExportCsv = useCallback(() => {
			const filename = `${policy.name ?? 'policy'}-issues.csv`;
			if (policyV2 && violationsData?.results) {
				const rows = violationsData.results.map((v) => ({
					resource_id: v.entity.id,
					resource_name: v.entity.title ?? '',
					conditions_not_met: v.violations
						.map((viol) => viol.policy_rule?.name ?? '')
						.filter(Boolean)
						.join('; '),
					score: String(v.total_score ?? ''),
					first_detected: v.violations[0]?.created_at
						? formatDateTimeWithAt(v.violations[0].created_at)
						: '',
				}));
				const csv = generateCsvFromResults(rows);
				saveBlob(new Blob([csv], { type: 'text/csv' }), filename);
			} else if (!policyV2 && issues.length > 0) {
				const rows = issues.map((issue) => ({
					resource_id: issue.resource.id,
					resource_name: issue.resource.title ?? '',
					issue_occurred: formatDateTimeWithAt(issue.occured_at),
					resolved: issue.resolved ? 'Yes' : 'No',
				}));
				const csv = generateCsvFromResults(rows);
				saveBlob(new Blob([csv], { type: 'text/csv' }), filename);
			}
		}, [policyV2, violationsData?.results, issues, policy.name]);

		const isLoading = policyV2 ? violationsLoading : issuesLoading;
		const data = policyV2 ? violationsData : issuesData;

		const hasNoResults = useMemo(() => {
			if (policyV2) {
				return violationsData?.results?.length === 0;
			}
			return issues.length === 0;
		}, [policyV2, violationsData?.results?.length, issues.length]);

		const hasViolations = useMemo(
			() =>
				policyV2 &&
				violationsData?.results &&
				violationsData.results.length > 0,
			[policyV2, violationsData?.results]
		);
		const hasIssues = useMemo(
			() => !policyV2 && issues.length > 0,
			[policyV2, issues.length]
		);

		const getFilterName = useCallback(() => {
			if (activeFilterType === 'integration') {
				return 'the selected integration';
			}
			if (activeFilterType === 'tag') {
				return 'the selected tag';
			}
			return null;
		}, [activeFilterType]);

		const hasActiveFilter = activeFilterType !== null;
		const showNoDataBanner = hasNoResults && hasActiveFilter;

		if (!isLoading && hasNoResults && !hasActiveFilter) {
			return (
				<Center py={theme.spacing['6xl']}>
					<Stack align="center">
						<Icon name="circleCheck" size="lg" color="icon/secondary/default" />
						<Title size="sm">No issues to resolve</Title>
					</Stack>
				</Center>
			);
		}

		return (
			<>
				<Stack pt="lg" spacing="md">
					<Group position="apart" align="center">
						<Group spacing="xs" align="center">
							{!activeFilterType && (
								<>
									<IntegrationSelector
										onChange={(value) => {
											setIntegrationId(value);
											if (value) {
												setActiveFilterType('integration');
											}
										}}
										initialValue={integrationId}
										allowEmptyOption
									/>
									<TagSelector
										onChange={(value) => {
											setTagId(value);
											if (value) {
												setActiveFilterType('tag');
											}
										}}
										initialValue={tagId}
										allowEmptyOption
									/>
								</>
							)}

							{activeFilterType === 'integration' && (
								<IntegrationSelector
									onChange={(value) => {
										setIntegrationId(value);
										if (!value) {
											setActiveFilterType(null);
										}
									}}
									initialValue={integrationId}
									allowEmptyOption
								/>
							)}
							{activeFilterType === 'tag' && (
								<TagSelector
									onChange={(value) => {
										setTagId(value);
										if (!value) {
											setActiveFilterType(null);
										}
									}}
									initialValue={tagId}
									allowEmptyOption
								/>
							)}
							{activeFilterType && (
								<Button
									variant="tertiary"
									size="md"
									onClick={handleClearFilters}
								>
									Clear filter
								</Button>
							)}
						</Group>
						<Button
							variant="tertiary"
							size="md"
							leftIconName="download"
							onClick={handleExportCsv}
							disabled={hasNoResults}
						>
							Export to CSV
						</Button>
					</Group>
					{showNoDataBanner && (
						<Banner
							tone="info"
							message={`No policy issues were found for ${getFilterName()}.`}
						/>
					)}
					{policyV2 && hasViolations && (
						<PolicyViolationsTable
							policy={policy}
							violations={violationsData?.results || []}
							onResourceClick={handleResourceClick}
							showScoreColumn={
								dqsV2 &&
								(policy as PolicyOut & { dqs_enabled?: boolean }).dqs_enabled
							}
							isLoading={isLoading}
							page={page}
							totalCount={data?.count ?? 0}
							hasNextPage={!!data?.meta.next_page}
							hasPreviousPage={!!data?.meta.previous_page}
							onPageChange={setPage}
						/>
					)}
					{!policyV2 && hasIssues && (
						<PolicySection
							spacing={false}
							title={
								<Flex pl="xs" className={classes.resourceColumn}>
									<Text>Resources at risk</Text>
									<Text className={classes.dateColumn}>Issue occurred</Text>
								</Flex>
							}
							content={
								<Flex
									h="50vh"
									direction="column"
									p={0}
									sx={{ overflowY: 'auto' }}
								>
									{issues.map((issue) => (
										<Box
											key={issue.id}
											className={classes.rowContainer}
											onClick={() => handleResourceClick(issue.resource.id)}
										>
											<Flex className={classes.resourceColumn}>
												<ResourceDisplay
													record={
														issue.resource as Pick<
															ISecodaEntity,
															| 'title'
															| 'integration'
															| 'search_metadata'
															| 'entity_type'
															| 'id'
														>
													}
												/>
												{issue.resolved && (
													<Group
														className={classes.resolveText}
														spacing="xs"
														noWrap
													>
														<Icon
															name="circleCheckFilled"
															fillColor="icon/success/default"
														/>
														<Text size="sm" color="text/secondary/default">
															Resolved
														</Text>
													</Group>
												)}
											</Flex>
											<Flex className={classes.dateColumn}>
												<Text size="sm">
													{formatDateTimeWithAt(issue.occured_at)}
												</Text>
											</Flex>
										</Box>
									))}
								</Flex>
							}
							footer={
								<ViolationTableFooter
									isLoading={isLoading}
									page={page}
									totalCount={data?.count ?? 0}
									hasPreviousPage={!!data?.meta.previous_page}
									hasNextPage={!!data?.meta.next_page}
									onPageChange={setPage}
								/>
							}
						/>
					)}
				</Stack>
				<PolicyIssueDrawer
					handleAcknowledgeOnClick={handleAcknowledgeOnClick}
					handleResolveOnClick={handleResolveOnClick}
					handleIgnoreOnClick={handleIgnoreOnClick}
					handleReopenOnClick={handleReopenOnClick}
					readOnly={readOnly}
				/>
			</>
		);
	}
);
